// Two worked layouts an author can hand to an assistant as a model.
//
// The brief (customHtmlBrief.js) states the rules; this states what a layout
// that follows them actually looks like. The two are deliberately different
// jobs: the brief is generated from the constants and is mostly prose, while
// these are complete, working slides that can be pasted into the two fields
// and will render — the thing people actually copy from.
//
// Both are built for a block that fills the slide, because that is the shape a
// new Custom HTML/CSS block is given (see frameForBlock in slideFrames.js), and
// they are sized in `cqw` and `%` throughout. That is not a stylistic
// preference: `.slide-custom-html` is a CSS container in the editor, in the
// exported HTML and in the PowerPoint/PDF raster alike, so `1cqw` means the
// same width in all four places. `vw` does not — in the editor it is a share of
// the browser window and in the export a share of the slide.

// The readability floor lives with the brief, so the two files cannot end up
// telling an author different numbers.
import {
  SLIDE_GEOMETRY, MIN_BODY_PT, MIN_BODY_CQW, PT_PER_CQW, BODY_CQW, BODY_PT
} from './customHtmlBrief';

const g = SLIDE_GEOMETRY;
const PT_PER_IN = 72;

/** A cqw size, as points, for the comments in the examples' CSS. */
const round = (cqw) => Math.round(cqw * PT_PER_CQW * 10) / 10;

/**
 * Example 1 — a picture, the points that go with it, and its note.
 *
 * The picture is referenced by slot name rather than pasted in as a URL, which
 * is what lets it export as a real asset. The panel behind the text is tinted
 * rather than the root being painted, so a slide background still shows.
 */
const PICTURE_HTML = `<!-- One root element. Fills the slide's content region. -->
<section class="lp-pic">

  <!-- LEFT: the words. A tinted panel, not a painted root, so a slide
       background still shows through around it. -->
  <div class="lp-pic__body">
    <p class="lp-pic__eyebrow">Section label</p>
    <h2 class="lp-pic__title">The heading for this slide</h2>
    <p class="lp-pic__lede">
      One or two lines that say what the picture is showing and why it is here.
    </p>
    <ul class="lp-pic__points">
      <li><strong>First idea.</strong> A sentence of detail.</li>
      <li><strong>Second idea.</strong> A sentence of detail.</li>
      <li><strong>Third idea.</strong> A sentence of detail.</li>
    </ul>
  </div>

  <!-- RIGHT: the picture. \`data-lp-image\` names an upload in this block's
       picture panel — write no src, the app fills in the file, its width and
       its height. Use the slot names the panel actually lists. -->
  <figure class="lp-pic__figure">
    <img data-lp-image="image-1" alt="Say what the picture shows.">
    <figcaption>Note. What the picture shows. From Author, Year.</figcaption>
  </figure>

</section>`;

const PICTURE_CSS = `/* ---------------------------------------------------------------
   Root: the slide's content region, and never taller.

   aspect-ratio rather than height:100% — it gives the layout a definite
   height in every destination, so the percentage heights inside it
   resolve the same way in the editor as in the export.
   --------------------------------------------------------------- */
.lp-pic {
  box-sizing: border-box;
  width: 100%;
  aspect-ratio: ${g.contentWidthPx} / ${g.contentHeightPx};
  max-height: 100%;
  overflow: hidden;          /* a long line clips instead of spilling */

  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.02fr);
  gap: 2.4cqw;
  padding: 3cqw 3.4cqw;

  background: transparent;   /* let the slide's background image show */
  color: inherit;            /* and its colour scheme */
  /* Body copy, read off a wall: ${BODY_PT}pt. The ${MIN_BODY_PT}pt floor is for
     captions, not for the sentences a class is reading. */
  font-size: ${BODY_CQW}cqw;
  line-height: 1.45;
}

.lp-pic *,
.lp-pic *::before,
.lp-pic *::after { box-sizing: inherit; }

/* ---------------------------------------------------------------
   LEFT: the tinted panel.

   Need contrast over a background image? Tint a panel like this one.
   Delete the background/border/padding here and the layout sits
   straight on the slide instead — both are fine, it is the ROOT that
   must stay transparent.
   --------------------------------------------------------------- */
.lp-pic__body {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1.1cqw;

  padding: 2.4cqw;
  background: rgb(255 255 255 / 0.78);
  border-radius: 1.2cqw;
}

.lp-pic__eyebrow {
  margin: 0;
  font-size: 1.35cqw;   /* ${round(1.35)}pt */
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  opacity: 0.65;
}

/* font-weight spelled out: the editor resets headings to inherit it, so a
   heading that leaves it to the browser is bold in the export and not bold
   in the editor. */
.lp-pic__title {
  margin: 0;
  font-size: 3.8cqw;   /* ${round(3.8)}pt */
  font-weight: 700;
  line-height: 1.12;
}

.lp-pic__lede {
  margin: 0;
  font-size: 2.2cqw;   /* ${round(2.2)}pt */
  line-height: 1.4;
  opacity: 0.82;
}

/* Same reason: the reset strips list markers, margin and padding. */
.lp-pic__points {
  list-style: disc outside;
  margin: 0.4cqw 0 0;
  padding-left: 1.5em;
  display: flex;
  flex-direction: column;
  gap: 0.7cqw;
}

/* ---------------------------------------------------------------
   RIGHT: the picture.

   The image fits the box the layout gives it rather than setting the
   box's size — object-fit crops, it never distorts. Design the box
   close to the upload's own proportions and it crops gently.
   --------------------------------------------------------------- */
.lp-pic__figure {
  min-width: 0;
  min-height: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.9cqw;
}

.lp-pic__figure img {
  flex: 1 1 auto;
  width: 100%;
  min-height: 0;
  object-fit: cover;
  /* cqw, not rem: a radius pinned to an absolute length is the one thing
     that does not grow with the slide, so the same corner reads squarer at
     one scale and rounder at another. */
  border-radius: 1.1cqw;
}

/* ${MIN_BODY_PT}pt — the floor, and a caption is what the floor is for. Quieten
   it with opacity, never by going smaller. */
.lp-pic__figure figcaption {
  flex: 0 0 auto;
  font-size: ${MIN_BODY_CQW}cqw;
  line-height: 1.35;
  opacity: 0.72;
}

/* ---------------------------------------------------------------
   A second upload as a backdrop instead of an <img>. Every upload is
   also a custom property, named after its slot.
   --------------------------------------------------------------- */
/*
.lp-pic__figure {
  background-image: var(--lp-image-2);
  background-size: cover;
  background-position: center;
  border-radius: 1.1cqw;
}
*/`;

/**
 * Example 2 — the links.
 *
 * PowerPoint and PDF get a picture of the layout, so an `<a href>` inside it
 * would ordinarily stop being a link. The exporters measure where each one
 * landed and lay a clickable region back over the picture, which is why this
 * example is worth having: it shows what shape an anchor has to be to survive
 * that, and what does not survive at all.
 */
const LINKS_HTML = `<!-- Every <a href="https://..."> in here stays clickable in the
     exported PowerPoint, the PDF and the HTML file. The layout becomes one
     picture and the app lays a clickable region back over each link, so
     what matters is that the anchor is a real element with real size. -->
<section class="lp-links">

  <div class="lp-links__intro">
    <p class="lp-links__eyebrow">Watch before class</p>
    <h2 class="lp-links__title">The heading for this slide</h2>
    <p class="lp-links__lede">
      One or two lines saying what to watch for while they watch it.
    </p>
    <div class="lp-links__note">
      <strong>As you watch:</strong> the question they should be able to
      answer afterwards.
    </div>
  </div>

  <div class="lp-links__actions">

    <!-- The main call to action. A block-level anchor with its own padding:
         the whole card is the clickable region, not just the words. -->
    <a class="lp-links__cta" href="https://www.youtube.com/watch?v=VIDEO_ID"
       target="_blank" rel="noopener noreferrer">
      <span class="lp-links__cta-icon" aria-hidden="true">&#9654;</span>
      <span class="lp-links__cta-text">
        <span class="lp-links__cta-label">Watch on YouTube</span>
        <span class="lp-links__cta-title">The video's title</span>
      </span>
      <span class="lp-links__cta-arrow" aria-hidden="true">&#8599;</span>
    </a>

    <!-- Secondary links. Same rule: one anchor per row, sized by its own
         padding so there is something to click. -->
    <a class="lp-links__row" href="https://example.org/reading"
       target="_blank" rel="noopener noreferrer">
      <span>Required reading</span>
      <span aria-hidden="true">&#8599;</span>
    </a>
    <a class="lp-links__row" href="https://example.org/worksheet"
       target="_blank" rel="noopener noreferrer">
      <span>Download the worksheet</span>
      <span aria-hidden="true">&#8599;</span>
    </a>

    <!-- A source line. An inline anchor works too — it is measured at the
         size of its own text, so keep it to a phrase rather than one
         character. -->
    <p class="lp-links__source">
      External video resource &middot;
      <a href="https://www.youtube.com/watch?v=VIDEO_ID"
         target="_blank" rel="noopener noreferrer">Author, Year</a>
    </p>

  </div>

</section>`;

const LINKS_CSS = `/* ---------------------------------------------------------------
   Root: same fit as the first example.
   --------------------------------------------------------------- */
.lp-links {
  box-sizing: border-box;
  width: 100%;
  aspect-ratio: ${g.contentWidthPx} / ${g.contentHeightPx};
  max-height: 100%;
  overflow: hidden;

  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
  gap: 2.6cqw;
  padding: 3cqw 3.4cqw;

  background: transparent;
  color: inherit;
  font-size: ${BODY_CQW}cqw;   /* ${BODY_PT}pt */
  line-height: 1.45;
}

.lp-links *,
.lp-links *::before,
.lp-links *::after { box-sizing: inherit; }

.lp-links__intro {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1.1cqw;
}

.lp-links__eyebrow {
  margin: 0;
  font-size: 1.35cqw;   /* ${round(1.35)}pt */
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  opacity: 0.65;
}

.lp-links__title {
  margin: 0;
  font-size: 3.6cqw;   /* ${round(3.6)}pt */
  font-weight: 700;
  line-height: 1.12;
}

.lp-links__lede {
  margin: 0;
  font-size: 2.2cqw;   /* ${round(2.2)}pt */
  line-height: 1.4;
  opacity: 0.82;
}

.lp-links__note {
  padding: 1.2cqw 1.4cqw;
  border-left: 0.35cqw solid rgb(120 120 120 / 0.45);
  background: rgb(255 255 255 / 0.7);
  border-radius: 0.7cqw;
  font-size: 1.7cqw;   /* ${round(1.7)}pt */
  line-height: 1.4;
}

/* ---------------------------------------------------------------
   The links.
   --------------------------------------------------------------- */
.lp-links__actions {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1.2cqw;
}

/* A block anchor with real padding. The clickable region the export lays
   over this is exactly the box you see, so give it a box worth hitting —
   an anchor wrapped tightly around one word gives a target the size of
   the word, in every format. */
.lp-links__cta {
  display: flex;
  align-items: center;
  gap: 1.2cqw;
  padding: 1.6cqw 1.8cqw;

  background: #ffffff;
  color: #b91c1c;
  border: 0.18cqw solid #f7b6b6;
  border-radius: 0.9cqw;
  text-decoration: none;
}

.lp-links__cta-icon {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.6cqw;
  height: 3.6cqw;
  background: #c62828;
  color: #ffffff;
  border-radius: 50%;
  font-size: ${MIN_BODY_CQW}cqw;
}

.lp-links__cta-text {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.lp-links__cta-label {
  font-size: 1.35cqw;   /* ${round(1.35)}pt */
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  opacity: 0.75;
}

.lp-links__cta-title {
  font-size: 2.4cqw;   /* ${round(2.4)}pt */
  font-weight: 700;
  line-height: 1.15;
}

.lp-links__cta-arrow { flex: 0 0 auto; font-size: 2.2cqw; }

.lp-links__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1cqw;
  padding: 1.15cqw 1.4cqw;

  background: rgb(255 255 255 / 0.82);
  color: inherit;
  border: 0.12cqw solid rgb(120 120 120 / 0.3);
  border-radius: 0.7cqw;
  font-size: 1.8cqw;   /* ${round(1.8)}pt */
  font-weight: 600;
  text-decoration: none;
}

.lp-links__source {
  margin: 0.3cqw 0 0;
  font-size: ${MIN_BODY_CQW}cqw;   /* ${MIN_BODY_PT}pt — the floor */
  line-height: 1.35;
  opacity: 0.7;
}

.lp-links__source a {
  color: inherit;
  text-decoration: underline;
}

/* ---------------------------------------------------------------
   :hover is fine — it works in the editor and in the exported HTML,
   and is simply not present in the PowerPoint or the PDF.
   --------------------------------------------------------------- */
.lp-links__cta:hover,
.lp-links__row:hover { background: #ffffff; }

/* ---------------------------------------------------------------
   A link does not have to look like a button.

   The clickable region the export lays over an anchor is invisible
   and adds no colour of its own, so the styling above is a choice,
   not a requirement. An anchor with no background at all is just as
   clickable — over a picture, over a heading, over part of a
   diagram. Give it a box and it works.

   In the editor and the exported HTML, :hover can reveal it. In
   PowerPoint and the PDF there is no hover, but the pointer still
   changes over it and the click still works.
   --------------------------------------------------------------- */
/*
.lp-links__plain {
  display: block;
  padding: 1.2cqw;
  background: transparent;
  border: 0;
  border-radius: 0.7cqw;
  color: inherit;
  text-decoration: none;
}
.lp-links__plain:hover { background: rgb(0 0 0 / 0.06); }
*/`;

/** The two examples, in the order they are offered. */
export const EXAMPLE_LAYOUTS = [
  {
    id: 'picture',
    name: 'Picture, points and a note',
    summary:
      'A two-column slide: a tinted text panel beside an uploaded picture with its APA note. '
      + 'Shows how to reference an upload by slot name, how to fit a picture to its box, and '
      + 'how to get contrast over a slide background without painting the root.',
    html: PICTURE_HTML,
    css: PICTURE_CSS
  },
  {
    id: 'links',
    name: 'Links that survive the export',
    summary:
      'A video call to action, two resource links and a linked source line. Shows the shape an '
      + 'anchor needs so the exporters can lay a working clickable region over it in PowerPoint, '
      + 'PDF and the HTML file.',
    html: LINKS_HTML,
    css: LINKS_CSS
  }
];

export const CUSTOM_HTML_EXAMPLES_FILENAME = 'lessonplanner-custom-slide-examples.md';

/** What this author has to hand, so the assistant uses real names. */
const inventory = ({ images, videos }) => {
  const lines = [];
  if (images.length) {
    lines.push(
      '**Pictures uploaded to this block** — reference these by name, and do not invent others:',
      '',
      ...images.map((image) => {
        const size = image?.width && image?.height ? ` (${image.width} x ${image.height} px)` : '';
        return `- \`${image.name}\`${size} — \`<img data-lp-image="${image.name}" alt="…">\` `
          + `or \`background-image: var(--lp-${image.name})\``;
      })
    );
  } else {
    lines.push(
      '**Nothing is uploaded to this block yet.** Example 1 references `image-1`. Either upload a '
      + 'picture to that slot in the block\'s picture panel, or say in your reply what the layout '
      + 'needs and roughly what shape it should be. Never invent a base64 string.'
    );
  }
  lines.push('');
  if (videos.length) {
    lines.push(
      '**Clips linked on this block** — `<a data-lp-video="name"></a>` resolves to a clickable '
      + 'poster card, which is an alternative to writing the button in example 2 by hand:',
      '',
      ...videos.map((video) => {
        const facts = [video?.title, video?.author].map((v) => String(v || '').trim()).filter(Boolean);
        return `- \`${video.name}\` — ${video.platform || 'video'}${facts.length ? `: ${facts.join(' · ')}` : ''}`;
      })
    );
  } else {
    lines.push(
      '**No clips are linked on this block.** Example 2 writes its own button, which is the right '
      + 'approach when you have the watch URL. Linking the clip in the block\'s video panel instead '
      + 'gets you a poster image as well.'
    );
  }
  return lines.join('\n');
};

const exampleSection = (example, index) => `## Example ${index + 1} — ${example.name}

${example.summary}

\`\`\`html
${example.html}
\`\`\`

\`\`\`css
${example.css}
\`\`\`
`;

/**
 * The whole examples file.
 *
 * Written for an assistant to read alongside the brief: the brief is the rules,
 * this is two layouts that keep them.
 */
export const buildCustomHtmlExamples = ({ images = [], videos = [], citations = [] } = {}) => {
  const uploads = (Array.isArray(images) ? images : [])
    .filter((image) => image && image.src)
    .map((image, index) => ({ ...image, name: String(image.name || `image-${index + 1}`) }));
  const clips = (Array.isArray(videos) ? videos : [])
    .filter((video) => video && video.url)
    .map((video, index) => ({ ...video, name: String(video.name || `video-${index + 1}`) }));
  const sources = Array.isArray(citations) ? citations.filter(Boolean) : [];

  return `# Custom HTML/CSS slide layout — two worked examples

Two complete layouts for one slide in LessonPlanner One. Each is a pair of
blocks — HTML and CSS — that pastes straight into the two fields in the block's
editor and renders as it stands. Copy the one closest to what you need and
replace the content; keep the fit, the units and the transparent root.

Read the build brief alongside this for the full rules (the tag allowlist, what
sanitizing removes, the background-image rules). This file is what following
them looks like.

## What both examples have in common

| | |
|---|---|
| Content region | ${g.slideWidthIn}in x ${g.contentHeightIn}in — ${g.contentWidthPx}px x ${g.contentHeightPx}px, ratio ${g.contentAspect}:1 |
| Root | one element, \`aspect-ratio\` to that shape, \`max-height: 100%\`, \`overflow: hidden\` |
| Background | transparent — panels inside are tinted instead |
| Units | \`cqw\` and \`%\` throughout |
| Body copy | **${BODY_CQW}cqw = ${BODY_PT}pt** — the size a class reads off the wall |
| Smallest type | **${MIN_BODY_CQW}cqw = ${MIN_BODY_PT}pt** — nothing on the slide is smaller |

**A new Custom HTML/CSS block already fills the slide**, so its layout box is
the content region above and these examples fit it exactly. If you drag the
block's handles to something smaller, the layout is laid out in *that* box
instead — the \`aspect-ratio\` line is then the wrong shape and should be
changed to match, or removed in favour of \`height: 100%\`.

### Why \`cqw\` and not \`vw\`

\`.slide-custom-html\` — the element the layout renders into — is a CSS
container in the editor, in the exported HTML file and in the picture the
PowerPoint and PDF exports are built from. So \`1cqw\` is 1% of the layout's own
width in all of them. \`1vw\` is not: in the editor it is a share of the browser
window, which is a different number on every screen. Sizes in \`cqw\` are the
reason a layout looks the same in all four places.

The same goes for \`border-radius\`, \`border-width\`, \`gap\` and \`box-shadow\`, not
just type. \`rem\` and \`px\` are safe only for hairlines you *want* pinned.

### Type sizes: ${BODY_PT}pt body, ${MIN_BODY_PT}pt floor

These slides are projected and read from the back of a room, so the sizes that
look fine on a monitor are the wrong sizes here. Two numbers matter:

- **Body copy and list items: \`${BODY_CQW}cqw\` (${BODY_PT}pt).** About what a
  PowerPoint body placeholder gives you on a slide this size. This is the
  target — the sentences a class is actually reading.
- **${MIN_BODY_PT}pt is the absolute floor**, and it is for captions, source
  lines and small labels only. Nothing on the slide goes under it: text below
  it is legible on the author's monitor and gone by the third row.

Quiet text is quietened with **opacity**, not with size.

In \`cqw\` that floor is exact rather than approximate. A block that fills the
slide is as wide as the slide, ${Math.round(g.slideWidthIn * PT_PER_IN)}pt, so:

    1cqw = ${PT_PER_CQW}pt        ${MIN_BODY_PT}pt = ${MIN_BODY_CQW}cqw

| Size | In points | Use for |
|---|---|---|
| \`3.8cqw\` | ${round(3.8)}pt | the slide's heading |
| \`2.4cqw\` | ${round(2.4)}pt | a sub-heading, a button's title |
| \`2.2cqw\` | ${round(2.2)}pt | the lead-in line under a heading |
| **\`${BODY_CQW}cqw\`** | **${BODY_PT}pt** | **body copy, list items — the default** |
| \`1.8cqw\` | ${round(1.8)}pt | a link row, a secondary line |
| \`1.35cqw\` | ${round(1.35)}pt | an uppercase eyebrow label |
| \`${MIN_BODY_CQW}cqw\` | ${MIN_BODY_PT}pt | **the floor** — captions, notes, source lines |

Both examples below are written at that scale: body copy at ${BODY_PT}pt, and
the smallest type in either is \`${MIN_BODY_CQW}cqw\` on a caption. If a layout
will not fit at these sizes, the slide has too much on it — cut the content
rather than the type size.

One catch worth knowing: that conversion assumes the block fills the slide,
which is how a new one starts. Scale the block down to half the slide's width
and \`1cqw\` is half as many points, so the floor becomes \`${(MIN_BODY_CQW * 2).toFixed(2)}cqw\`.
Keeping the block full-bleed keeps the arithmetic simple.

## What this block has to work with

${inventory({ images: uploads, videos: clips })}
${sources.length ? `\n**${sources.length} source${sources.length === 1 ? ' is' : 's are'} cited on this block.** The app builds the deck's reference\nlist itself; place only the short in-text note in the layout, as the brief\nspells out.\n` : ''}
${exampleSection(EXAMPLE_LAYOUTS[0], 0)}
${exampleSection(EXAMPLE_LAYOUTS[1], 1)}
## Links, and what happens to them

A layout becomes a single picture in the PowerPoint and PDF exports. The app
measures where each \`<a href>\` landed in that picture and lays a clickable
region back over it, so links keep working in all four destinations. Two things
follow from that:

- **Give an anchor a box.** The clickable region is the anchor's own rectangle.
  A \`<a>\` with padding, or one laid out as a flex row, gives something worth
  clicking; an anchor wrapped around a single arrow character gives a target
  the size of that character.
- **Only real addresses travel.** \`https:\` and \`mailto:\` links are kept.
  Anything else — an in-page \`#anchor\`, a \`javascript:\` URL — is not a place
  the exported file can go, and is dropped.
- **A link does not have to look like a button.** The region laid over it is
  invisible and adds no colour of its own, so how the link looks is entirely
  your styling. Example 2 draws a coloured card because a "watch this" call to
  action wants to be seen, but an anchor with \`background: transparent\` and no
  border is exactly as clickable — over a picture, a heading, or part of a
  diagram. In the editor and the exported HTML you can reveal it on
  \`:hover\`; in PowerPoint and the PDF there is no hover, but the pointer still
  changes over it and the click still works.

\`<iframe>\` is removed by the sanitizer, so an embedded player is not an option
in any destination. A poster image inside a link is, and is what
\`data-lp-video\` gives you.
`;
};
